import { Badge as AntdBadge, Tooltip } from "antd/es";
import { BadgeProps as AntdBadgeProps } from "antd/es/badge";
import _ from "lodash";
import React, { PureComponent, ReactNode } from "react";
import ReactDOM from "react-dom";
import { createPortal } from "react-dom";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { CommonProps } from "../common-props";
import { Icon, IconLibrary, IconType } from "../icon/icon";

interface BadgeProps extends AntdBadgeProps {
  value: { count: string };
  tooltip?: string;
  icon?: string;
  iconLibrary?: IconLibrary;
  iconType?: IconType;
}

interface BadgeState {}

class Badge extends PureComponent<BadgeProps & CommonProps, BadgeState> {
  private indicatorRef = React.createRef<HTMLDivElement>();
  private badgeElement: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private rafId: number | null = null;
  // Cached once on mount — the badge's DOM position doesn't change, so
  // there's no need to walk ancestors on every frame.
  private insideOverlay = false;

  constructor(props: BadgeProps) {
    super(props);
    this.state = {};
  }

  private shouldUsePortal(): boolean {
    return !!this.props.children && !this.props.tooltip;
  }

  componentDidMount() {
    if (this.shouldUsePortal()) {
      // eslint-disable-next-line react/no-find-dom-node
      const node = ReactDOM.findDOMNode(this);
      if (node instanceof HTMLElement) {
        this.badgeElement = node;
        this.insideOverlay = this.isInsideAntOverlay(node);
        // z-index only depends on overlay membership which is fixed for the
        // lifetime of this component, so set it once here instead of on
        // every updateIndicatorPosition call.
        const indicator = this.indicatorRef.current;
        if (indicator) {
          indicator.style.zIndex = this.insideOverlay ? "9999" : "999";
        }
        this.setupPositionTracking();
      }
    }
  }

  componentDidUpdate() {
    if (this.shouldUsePortal()) {
      this.updateIndicatorPosition();
    }
  }

  componentWillUnmount() {
    this.cleanupPositionTracking();
  }

  private setupPositionTracking() {
    this.updateIndicatorPosition();
    window.addEventListener("scroll", this.schedulePositionUpdate, true);
    window.addEventListener("resize", this.schedulePositionUpdate);
    window.addEventListener("transitionend", this.schedulePositionUpdate, true);
    window.addEventListener("animationend", this.schedulePositionUpdate, true);

    if (this.badgeElement) {
      this.resizeObserver = new ResizeObserver(this.schedulePositionUpdate);
      this.resizeObserver.observe(this.badgeElement);
    }
  }

  private cleanupPositionTracking() {
    window.removeEventListener("scroll", this.schedulePositionUpdate, true);
    window.removeEventListener("resize", this.schedulePositionUpdate);
    window.removeEventListener("transitionend", this.schedulePositionUpdate, true);
    window.removeEventListener("animationend", this.schedulePositionUpdate, true);
    this.resizeObserver?.disconnect();
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  private schedulePositionUpdate = () => {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(this.updateIndicatorPosition);
  };

  private isInsideAntOverlay(element: HTMLElement | null): boolean {
    let current = element;
    while (current) {
      if (current.classList && (current.classList.contains("ant-drawer") || current.classList.contains("ant-modal"))) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private updateIndicatorPosition = () => {
    const indicator = this.indicatorRef.current;
    if (!this.badgeElement || !indicator) return;
    const rect = this.badgeElement.getBoundingClientRect();
    indicator.style.top = `${rect.top}px`;
    indicator.style.left = `${rect.right}px`;
  };

  getClassName(style: React.CSSProperties) {
    if (!style) return;
    if (style.textDecoration === "underline") {
      return "kuika-badge-component__text-decoration-underline";
    }
    if (style.textDecoration === "line-through") {
      return "kuika-badge-component__text-decoration-line-through";
    }
  }

  hasUserBorder = (style: React.CSSProperties | undefined): boolean => {
    if (!style) return false;
    return !!(
      style.border ||
      style.borderWidth ||
      style.borderTop ||
      style.borderRight ||
      style.borderBottom ||
      style.borderLeft ||
      style.borderTopWidth ||
      style.borderRightWidth ||
      style.borderBottomWidth ||
      style.borderLeftWidth
    );
  };

  buildIconPill = (): ReactNode => {
    if (!this.props.icon) {
      return undefined;
    }

    const userStyle: React.CSSProperties = this.props.style ? { ...this.props.style } : {};
    delete (userStyle as any).textAlign;
    const iconColor = userStyle.color ?? "#fff";
    const iconFontSize = userStyle.fontSize ?? "12px";
    const badgeColor = (this.props as any).color ?? "#ff4d4f";

    const pill = (
      <div
        style={{
          backgroundColor: badgeColor,
          borderRadius: "10px",
          minWidth: "20px",
          height: "20px",
          padding: "0 6px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: "12px",
          lineHeight: "20px",
          whiteSpace: "nowrap",
          boxShadow: "0 0 0 1px #fff",
          ...userStyle,
          cursor: this.props.tooltip ? "pointer" : userStyle.cursor,
          boxSizing: this.hasUserBorder(userStyle) ? "content-box" : "border-box"
        }}
      >
        <Icon
          iconName={this.props.icon}
          iconLibrary={this.props.iconLibrary}
          iconType={this.props.iconType}
          style={{ color: iconColor, fontSize: iconFontSize }}
        />
      </div>
    );

    if (this.props.tooltip) {
      const TooltipComponent = Tooltip as any;
      return (
        <TooltipComponent color={"white"} title={this.props.tooltip}>
          {pill}
        </TooltipComponent>
      );
    }
    return pill;
  };

  getProps = () => {
    const props: any = _.clone(this.props);
    // `getClassName` returns ONLY a text-decoration class (or undefined). Assigning
    // it directly overwrote the cloned `this.props.className` (the generator's
    // `kuika-style-<id>` that state CSS targets) whenever the decoration class
    // existed. Merge so the caller class survives alongside the decoration class.
    const className = this.getClassName(props.style);
    if (className) {
      props.className = `${this.props.className ?? ""} ${className}`.trim();
    }
    if (props.value) {
      props.count = props.value.count !== undefined ? props.value.count : props.value;
    }

    if (this.props.dot) {
      props.count = null;
    }

    props.style = this.props.style ? { ...this.props.style } : undefined;

    if (this.props.icon) {
      props.count = this.buildIconPill();
      props.style = undefined;
    } else if (props.style) {
      delete props.style.textAlign;
      // Designer forwards `display: inline` onto .ant-badge-count, which collapses
      // its box (no aspect-ratio, no padding-y, no width/height) and breaks the
      // round single-character rendering. Antd's own stylesheet sets the right
      // display, so drop the inline override.
      delete props.style.display;
      if (this.props.tooltip) {
        props.style.cursor = "pointer";
      }
      if (this.hasUserBorder(props.style)) {
        props.style.boxSizing = "content-box";
      }
    } else if (this.props.tooltip) {
      props.style = { cursor: "pointer" };
    }

    if (this.props.tooltip && !this.props.icon) {
      props.title = this.props.tooltip;
    }

    if (props.tooltip !== undefined) {
      delete props.tooltip;
    }
    if (props.icon !== undefined) {
      delete props.icon;
    }
    if (props.iconLibrary) {
      delete props.iconLibrary;
    }
    if (props.iconType) {
      delete props.iconType;
    }
    return props;
  };

  render(): ReactNode {
    if (!this.props.children) {
      return (
        <div className="kuika-badge-component__right-top-align">
          <AntdBadge {...this.getProps()}>{this.props.children}</AntdBadge>
        </div>
      );
    }

    const props = this.getProps();

    if (!this.shouldUsePortal()) {
      return <AntdBadge {...props}>{this.props.children}</AntdBadge>;
    }

    return (
      <>
        <AntdBadge {...props} count={0} showZero={false} dot={false}>
          {this.props.children}
        </AntdBadge>
        {createPortal(
          <div
            ref={this.indicatorRef}
            style={{
              position: "fixed",
              top: -9999,
              left: -9999,
              transform: "translate(-50%, -50%)",
              zIndex: 999,
              pointerEvents: "none"
            }}
          >
            <AntdBadge
              count={props.count}
              dot={props.dot}
              showZero={props.showZero}
              overflowCount={props.overflowCount}
              color={props.color}
              status={props.status}
              style={props.style}
              className={props.className}
              title={props.title}
            />
          </div>,
          document.body
        )}
      </>
    );
  }
}

const badge = withCommonEvents(Badge);
export { badge as Badge };
